import { UserGroupIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { User } from 'shared';
import { ActivitySummary } from '@/components/features/teams/ActivitySummary';
import { TicketBoard } from '@/components/features/teams/board/TicketBoard';
import { MobileBoard } from '@/components/features/teams/board/MobileBoard';
import { HierarchyTree } from '@/components/features/teams/hierarchy/HierarchyTree';
import { MobileHierarchy } from '@/components/features/teams/hierarchy/MobileHierarchy';
import { TeamsToolbar } from '@/components/features/teams/TeamsToolbar';
import { WorkloadCalendar, MobileWorkloadCalendar } from '@/components/features/teams/workload';
import { UsersContent } from '@/components/features/teams/UsersContent';
import { useIsMobile } from '@/hooks/use-mobile';
import { teamsApi } from '@/lib/api-client';

interface TeamsSearchParams {
  search?: string;
  groups?: string;
  sort?: string;
  order?: 'asc' | 'desc';
  view?: 'card' | 'list' | 'hierarchy' | 'board' | 'workload';
}

function TeamsPage() {
  const { t } = useTranslation('teams');
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const searchParams = useSearch({ from: '/_authenticated/teams' }) as TeamsSearchParams;

  const search = searchParams.search || '';
  const groupsParam = searchParams.groups || '';
  const selectedGroups = useMemo(
    () => (groupsParam ? groupsParam.split(',').filter(Boolean) : []),
    [groupsParam],
  );
  const sort = searchParams.sort || 'shift';
  const order = (searchParams.order || 'desc') as 'asc' | 'desc';
  const view = (searchParams.view || 'card') as 'card' | 'list' | 'hierarchy' | 'board' | 'workload';
  const [dueTodayOnly, setDueTodayOnly] = useState(false);

  const updateParams = (updates: Partial<TeamsSearchParams>) => {
    navigate({
      to: '/teams',
      search: { ...searchParams, ...updates } as any,
    });
  };

  const {
    data: usersResponse,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['teams', search, selectedGroups, sort, order],
    queryFn: async ({ pageParam = 1 }) => {
      const now = new Date();
      const clientDate = `${now.getFullYear()}-${(now.getMonth() + 1).toString().padStart(2, '0')}-${now.getDate().toString().padStart(2, '0')}`;
      const clientTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

      const response = (await teamsApi.getAll({
        page: pageParam,
        limit: 50,
        search: search || undefined,
        includeEffort: true,
        prioritizeShift: true,
        clientDate,
        clientTime,
        includeQualifications: true,
        includeTickets: true,
      })) as
        | User[]
        | {
            data: User[];
            pagination: { page: number; limit: number; totalItems: number; totalPages: number };
          };

      if (Array.isArray(response)) {
        return { data: response, totalPages: 1, totalItems: response.length };
      }

      return {
        data: response.data || [],
        totalPages: response.pagination?.totalPages || 1,
        totalItems: response.pagination?.totalItems || response.data?.length || 0,
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalPages = lastPage.totalPages || 1;
      const nextPage = allPages.length + 1;
      return nextPage <= totalPages ? nextPage : undefined;
    },
    initialPageParam: 1,
  });

  const allUsers = useMemo(() => {
    if (!usersResponse?.pages) return [];
    return usersResponse.pages.flatMap((page) => page.data || []);
  }, [usersResponse]);

  return (
    <div className="flex flex-col gap-3 md:gap-4">
      <div className="hidden md:flex items-center justify-between">
        <div className="flex items-center gap-3">
          <HugeiconsIcon icon={UserGroupIcon} size={24} className="text-primary" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('title')}</h1>
            <p className="text-sm text-muted-foreground">{t('description')}</p>
          </div>
        </div>
      </div>

      <TeamsToolbar
        search={search}
        onSearchChange={(s) => updateParams({ search: s || undefined })}
        selectedGroups={selectedGroups}
        onGroupsChange={(groups) => updateParams({ groups: groups.join(',') || undefined })}
        view={view}
        onViewChange={(v) => updateParams({ view: v })}
        sort={sort}
        onSortChange={(s) => updateParams({ sort: s })}
        order={order}
        onOrderChange={(o) => updateParams({ order: o })}
        dueTodayOnly={dueTodayOnly}
        onDueTodayChange={setDueTodayOnly}
      />

      {view === 'hierarchy' ? (
        isMobile ? (
          <MobileHierarchy search={search} />
        ) : (
          <HierarchyTree search={search} />
        )
      ) : view === 'board' ? (
        isMobile ? (
          <MobileBoard sort={sort} order={order} dueTodayOnly={dueTodayOnly} />
        ) : (
          <TicketBoard sort={sort} order={order} dueTodayOnly={dueTodayOnly} />
        )
      ) : view === 'workload' ? (
        isMobile ? (
          <MobileWorkloadCalendar />
        ) : (
          <WorkloadCalendar />
        )
      ) : (
        <>
          <ActivitySummary users={allUsers} />

          <UsersContent
            users={allUsers}
            view={view}
            isLoading={isLoading}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            sort={sort}
            order={order}
          />
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/teams')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      search: (search.search as string) || undefined,
      groups: (search.groups as string) || undefined,
      sort: (search.sort as string) || 'shift',
      order: ((search.order as string) || 'desc') as 'asc' | 'desc',
      view: ((search.view as string) || 'card') as 'card' | 'list' | 'hierarchy' | 'board' | 'workload',
    };
  },
  component: TeamsPage,
});
